<script>
import SelectFish from '@/components/catalog/SelectFish.vue'
import SelectSource from '@/components/catalog/SelectSource.vue'
import AqField from '@/components/aqua/AqField.vue'

export default {
	name: 'GrownFishModal',
	components: {
		SelectFish,
		SelectSource,
		AqField,
	},
	props: {
		fish: {
			type: Object,
			default: null,
		},
		isNotEditable: {
			type: Boolean,
			default: false,
		},
		design_capacities: {
			type: Array,
			default() {
				return []
			},
		},
	},
	emits: ['close', 'accept'],
	data() {
		return {
			modelValue: {
				type: null,
				design_capacity: null,
				water_supply_source_id: null,
				water_supply_source_volume: null,
				actual_production_by_species: null,
				sold_volume: null,
				juvenile_production_by_age: null,
				juvenile_sold: null,
				broodstock_production: null,
				broodstock_sold: null,
				feed_consumption_tons: null,
				feed_info: null,
			},
		}
	},
	computed: {
		confinesValueFish() {
			return (
				this.design_capacities?.map((dc) => ({
					id: dc.fish_id,
				})) || []
			)
		},
		modalTitle() {
			if (this.isNotEditable) return this.$t('titles.viewingNewGrown')
			if (this.fish) return this.$t('titles.editingNewGrown')
			return this.$t('titles.addingNewGrown')
		},
		groups() {
			return [
				{ key: 'production', fields: ['actual_production_by_species', 'sold_volume'] },
				{ key: 'juvenile', fields: ['juvenile_production_by_age', 'juvenile_sold'] },
				{ key: 'broodstock', fields: ['broodstock_production', 'broodstock_sold'] },
				{ key: 'feed', fields: ['feed_consumption_tons', 'feed_info'] },
			]
		},
	},
	watch: {
		'modelValue.type': function () {
			if (this.design_capacities?.length && this.modelValue?.type) {
				const dc = this.design_capacities.find((dc) => dc.fish_id === this.modelValue?.type?.id)
				if (dc) {
					this.modelValue.design_capacity = parseFloat(dc.volume)
				}
			}
		},
	},
	methods: {
		opened() {
			const fh = this.$props.fish
			if (!fh) return
			this.modelValue.type = fh?.type || null
			this.modelValue.water_supply_source_id =
				fh?.water_supply_source || fh?.water_supply_source_id || null
			this.modelValue.design_capacity = fh?.design_capacity || null
			this.modelValue.water_supply_source_volume = fh?.water_supply_source_volume || null
			this.modelValue.actual_production_by_species = fh?.actual_production_by_species || null
			this.modelValue.sold_volume = fh?.sold_volume || null
			this.modelValue.juvenile_production_by_age = fh?.juvenile_production_by_age || null
			this.modelValue.juvenile_sold = fh?.juvenile_sold || null
			this.modelValue.broodstock_production = fh?.broodstock_production || null
			this.modelValue.broodstock_sold = fh?.broodstock_sold || null
			this.modelValue.feed_consumption_tons = fh?.feed_consumption_tons || null
			this.modelValue.feed_info = fh?.feed_info || null
		},
		clear() {
			this.modelValue.type = null
			this.modelValue.water_supply_source_id = null
			this.modelValue.water_supply_source_volume = null
			this.modelValue.actual_production_by_species = null
			this.modelValue.sold_volume = null
			this.modelValue.juvenile_production_by_age = null
			this.modelValue.juvenile_sold = null
			this.modelValue.broodstock_production = null
			this.modelValue.broodstock_sold = null
			this.modelValue.feed_consumption_tons = null
			this.modelValue.feed_info = null
		},
		display(value) {
			return value === null || value === undefined || value === '' ? '—' : value
		},
	},
}
</script>

<template>
	<modal
		:name="'GrownFishModal'"
		class="modal__block modal__block--middle"
		:width="760"
		:min-width="320"
		:adaptive="true"
		:scrollable="true"
		height="auto"
		@opened="opened"
		@closed="clear"
	>
		<div class="aq-page aq-fish-modal">
			<header class="aq-fish-modal__head">
				<h2 class="aq-fish-modal__title">{{ modalTitle }}</h2>
				<button
					type="button"
					class="aq-fish-modal__close"
					:aria-label="$t('button.close')"
					@click.stop.prevent="$emit('close'), clear()"
				>
					<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
						<path d="M6 6l12 12M18 6L6 18" />
					</svg>
				</button>
			</header>

			<div class="aq-fish-modal__body">
				<div class="aq-grid">
					<AqField :label="$t('labels.objectAquaItem')" :required="!isNotEditable">
						<div v-if="isNotEditable" class="aq-value">{{ modelValue.type ? modelValue.type.title : '—' }}</div>
						<SelectFish
							v-else
							class="aq-legacy"
							:value="modelValue.type"
							:required="true"
							:with-title="false"
							:multiple="false"
							:confines-value="confinesValueFish"
							:placeholder="$t('filters.select_fish')"
							@input="modelValue.type = $event"
						/>
					</AqField>
					<AqField :label="$t('labels.design_capacity')" :hint="isNotEditable ? '' : $t('aqua.ui.grown.capacity_auto')">
						<div class="aq-value aq-value--accent">
							{{ display(modelValue.design_capacity) }}
							<small v-if="modelValue.design_capacity">{{ $t('aqua.ui.grown.unit_t') }}</small>
						</div>
					</AqField>
				</div>

				<section v-for="group in groups" :key="group.key" class="aq-fish-modal__group">
					<h3 class="aq-fish-modal__group-title">{{ $t(`aqua.ui.grown.groups.${group.key}`) }}</h3>
					<div class="aq-grid">
						<AqField v-for="field in group.fields" :key="field" :label="$t(`labels.${field}`)">
							<div v-if="isNotEditable" class="aq-value">{{ display(modelValue[field]) }}</div>
							<input
								v-else
								v-model="modelValue[field]"
								type="number"
								step=".01"
								min="0"
								inputmode="decimal"
								class="aq-input"
							/>
						</AqField>
					</div>
				</section>

				<section class="aq-fish-modal__group">
					<h3 class="aq-fish-modal__group-title">{{ $t('aqua.ui.grown.groups.water') }}</h3>
					<div class="aq-grid">
						<AqField :label="$t('labels.sourceWaterDelivery')">
							<div v-if="isNotEditable" class="aq-value">
								{{ modelValue.water_supply_source_id && modelValue.water_supply_source_id.title ? modelValue.water_supply_source_id.title : '—' }}
							</div>
							<SelectSource
								v-else
								class="aq-legacy"
								:value="modelValue.water_supply_source_id"
								:required="true"
								:with-title="false"
								:multiple="false"
								@input="modelValue.water_supply_source_id = $event"
							/>
						</AqField>
						<AqField :label="$t('labels.sourceWaterVolume')">
							<div v-if="isNotEditable" class="aq-value">{{ display(modelValue.water_supply_source_volume) }}</div>
							<input
								v-else
								v-model="modelValue.water_supply_source_volume"
								type="number"
								step=".01"
								min="0"
								inputmode="decimal"
								class="aq-input"
							/>
						</AqField>
					</div>
				</section>
			</div>

			<footer class="aq-fish-modal__foot">
				<button type="button" class="aq-btn" @click.stop.prevent="$emit('close'), clear()">
					{{ isNotEditable ? $t('button.close') : $t('button.cancel') }}
				</button>
				<button
					v-if="!isNotEditable"
					type="button"
					class="aq-btn aq-btn--primary"
					:disabled="!modelValue.type"
					@click="$emit('accept', modelValue), clear()"
				>
					{{ $t('button.save') }}
				</button>
			</footer>
		</div>
	</modal>
</template>

<style scoped>
.aq-fish-modal {
	display: flex;
	flex-direction: column;
	background: var(--aq-surface);
	text-align: left;
}
.aq-fish-modal__head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 22px 28px;
	border-bottom: 1px solid var(--aq-border);
}
.aq-fish-modal__title {
	margin: 0;
	font-size: 20px;
	font-weight: 700;
	line-height: 1.3;
	color: var(--aq-ink);
}
.aq-fish-modal__close {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 44px;
	height: 44px;
	border-radius: 10px;
	color: var(--aq-muted);
	transition: background 0.2s var(--aq-ease), color 0.2s var(--aq-ease);
}
.aq-fish-modal__close:hover {
	background: var(--aq-bg);
	color: var(--aq-ink);
}
.aq-fish-modal__close:focus-visible {
	outline: none;
	box-shadow: 0 0 0 3px rgba(21, 127, 238, 0.25);
}
.aq-fish-modal__body {
	padding: 20px 28px 8px;
}
.aq-fish-modal__body .aq-grid {
	grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
	align-items: end;
}
.aq-fish-modal__group {
	margin-top: 22px;
	padding-top: 18px;
	border-top: 1px solid var(--aq-border);
}
.aq-fish-modal__group-title {
	margin: 0 0 12px;
	color: var(--aq-primary);
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
}
.aq-fish-modal__foot {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	padding: 18px 28px 24px;
}
.aq-value {
	min-height: 44px;
	padding: 11px 0;
	border-bottom: 1px dashed var(--aq-border-strong);
	color: var(--aq-ink);
	font-size: 15px;
	font-weight: 500;
	word-break: break-word;
}
.aq-value--accent {
	padding: 11px 14px;
	border: 0;
	border-radius: 10px;
	background: var(--aq-primary-soft);
	color: var(--aq-primary);
	font-weight: 600;
}
.aq-value small {
	margin-left: 4px;
	font-size: 13px;
	font-weight: 500;
}
.aq-legacy ::v-deep .multiselect__tags {
	min-height: 44px;
	padding-top: 10px;
	border: 1px solid var(--aq-border-strong) !important;
	border-radius: 10px;
	transition: border-color 0.2s var(--aq-ease);
}
.aq-legacy ::v-deep .multiselect__tags:hover,
.aq-legacy ::v-deep .multiselect--active .multiselect__tags {
	border-color: var(--aq-primary) !important;
}
.aq-legacy ::v-deep .multiselect__single,
.aq-legacy ::v-deep .multiselect__placeholder {
	font-size: 15px;
}
@media (max-width: 640px) {
	.aq-fish-modal__head,
	.aq-fish-modal__body,
	.aq-fish-modal__foot {
		padding-left: 16px;
		padding-right: 16px;
	}
}
</style>
